import React, { useContext, useRef } from "react"
import { SdDetectionConfig, SdDetectionJSON } from "../../../../Types/SdDetection"
import { DimensionsContext, DimensionsProvider } from "../../../../../../../../Providers/DimensionsProvider"
import { useRecoilState, useRecoilValue } from "recoil"
import { SdDetectionConfigAtom } from "../../../../Atoms/SdDetection"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { D3SdDetection } from "../D3/D3SdDetection"
import { canOpenAnnotationsModalAtom, annotationZOrderAtom } from "../../../../Atoms/Annotations"
import { useInProgressAnnotation } from "../../TimeSeriesGraphGroup/React/useInProgressAnnotation"
import { hotkeyActions } from "../../../../Types/KeyboardShortcut"
import { selectedLayoutIdAtom } from "../../../../Atoms/Layout"
import { SDDetectionWindowSettings } from "../../../../../../../../Managers/VisualizationManager/WindowSettings/SDDetectionWindowSettings"
import { useD3KeyboardShortcuts } from "../../../../Hooks/useD3KeyboardShortcuts"
import { useD3CheckReloadData } from "../../../../Hooks/useD3CheckReloadData"
import { pageManagerRegistry } from "../../../../Data/PageManagerRegistry"
import { viewScaleRegistry } from "../../../../Data/ViewScaleRegistry"
import { useD3UpdateVisibleTraces } from "../../../../Hooks/useD3UpdateVisibleTraces"
import { fileScaleRegistry } from "../../../../Data/FileScaleRegistry"
import { useValidateEEGMontage } from "../../../../Hooks/useValidateEEGMontage"
import { useLayoutService } from "../../../../../../../../Hooks/useLayoutService"
import { useD3Controller } from "../../../../Hooks/useD3Controller"
import { useSocketProvider } from "../../../../../../../../Providers/SocketProvider"
import { useEventReview } from "../../../../../../../../Providers/EventReviewProvider"
import { SDTimeSeriesPageManager } from "../../../../Data/SDTimeSeriesPageManager"
import { VisualizationComponent } from "../../../../VisualizationComponent"
import { useAnnotationContextMenu } from "../../../ContextMenus/useAnnotationContextMenu"
import { isResolvableEEGLead } from "../../../../Types/EEG"

type SdDetectionProps = {
    windowId: string,
    json: SdDetectionJSON
}

export const SdDetection = (props: SdDetectionProps) => {
    const { montageMapping } = useRecoilValue(currentPatientFileInfoAtom)
    const layoutId = useRecoilValue(selectedLayoutIdAtom)
    const visualizationId = { layoutId: layoutId as string, windowId: props.windowId }
    const atom = SdDetectionConfigAtom(visualizationId)
    const [config, setRootConfig] = useRecoilState(atom)
    const { modifyDatabaseWindowProperty } = useLayoutService()

    // Auto-select montage if null - must run before channel validation check
    useValidateEEGMontage({
        montage: config.montage,
        defaultMontageId: 6,
        updateMontage: montage => {
            // Update local state unconditionally (allows initial selection)
            setRootConfig(previous => ({ ...previous, montage }))

            // Only update database if already initialized
            if (config.hasInitialized) {
                modifyDatabaseWindowProperty("montage", visualizationId.layoutId, visualizationId.windowId, montage)
            }
        }
    })

    // Check if patient has SD detection data
    const patientHasSDData = montageMapping.size > 0

    // Check if selected montage has valid channels
    const hasValidChannels = (config.montage?.channels.filter(
        ({ first, second }) => (
            isResolvableEEGLead(first, montageMapping) &&
            isResolvableEEGLead(second, montageMapping)
        )
    ).length ?? 0) > 0

    // Determine empty state message
    let emptyStateMessage: string | null = null
    if (!patientHasSDData) {
        emptyStateMessage = "This recording doesn't contain EEG"
    } else if (!hasValidChannels) {
        emptyStateMessage = "No channels available for the selected montage"
    }

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 0 }}>
                <SDDetectionWindowSettings windowId={props.windowId} />
            </div>
            <div style={{ flex: 1 }}>
                {emptyStateMessage === null ? (
                    <DimensionsProvider>
                        <SdDetectionVisualization {...props} />
                    </DimensionsProvider>
                ) : (
                    <div style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#666"
                    }}>
                        {emptyStateMessage}
                    </div>
                )}
            </div>
        </div>
    )
}


const SdDetectionVisualization = (props: SdDetectionProps) => {
    const eventReviewProvider = useEventReview()
    const dimensions = useContext(DimensionsContext)
    const { inProgressAnnotation } = useInProgressAnnotation(props.windowId)
    const { getDataQuerySocket } = useSocketProvider()
    const domNode = useRef<HTMLDivElement>(null)
    const layoutId = useRecoilValue(selectedLayoutIdAtom)
    const visualizationId = { layoutId: layoutId as string, windowId: props.windowId }
    const atom = SdDetectionConfigAtom(visualizationId)
    const config = useRecoilValue(atom)
    const { fileStartDate, fileEndDate, montageMapping } = useRecoilValue(currentPatientFileInfoAtom)
    const annotationZOrder = useRecoilValue(annotationZOrderAtom(props.windowId))
    const { AnnotationContextMenu, openAnnotationContextMenu } = useAnnotationContextMenu(props.windowId)

    const annotations = eventReviewProvider.annotationsForRendering
    const enableOpeningAnnotationsModal = useRecoilValue(canOpenAnnotationsModalAtom)

    const [viewStart, viewEnd] = config.viewScale.domain()
    const viewDuration = viewEnd.getTime() - viewStart.getTime()

    const viewScale = viewScaleRegistry.get(visualizationId, VisualizationComponent.SD_DETECTION, { fileStartDate, fileEndDate, viewDuration: props.json.viewDuration })

    const d3Controller = useD3Controller<D3SdDetection, SdDetectionConfig, SdDetectionJSON, SDTimeSeriesPageManager>({
        atom,
        nodeRef: domNode,
        initProps: props.json,
        pageManager: pageManagerRegistry.get(visualizationId, VisualizationComponent.SD_DETECTION, new SDTimeSeriesPageManager(), getDataQuerySocket),
        props: {
            id: props.windowId,
            windowType: VisualizationComponent.SD_DETECTION,
            viewScale,
            fileScale: fileScaleRegistry.get(visualizationId, VisualizationComponent.SD_DETECTION, fileStartDate, fileEndDate),
            annotations,
            inProgressAnnotation,
            montageMapping,
            enableOpeningAnnotationsModal,
            currentlyReviewedAnnotation: eventReviewProvider.currentAnnotation,
            hideAnnotations: false,
            annotationZOrder
        },
        d3ControllerConstructor(node, config, pageManager, callbacks) {
            return new D3SdDetection(node, config, pageManager, { ...callbacks, openAnnotationContextMenu, setSelectedAnnotation: () => {} })
        },
        extractJSON(config) {
            return {
                viewDuration,
                playbackSpeed: config.playbackSpeed,
                montage: config.montage,
                eegConfig: config.eegConfig,
                overlayEEGConfig: config.overlayEEGConfig,
                isLinked: config.isLinked,
                autoDetectionEnabled: config.autoDetectionEnabled
            }
        }
    })

    useD3CheckReloadData({
        d3Controller,
        clearDataIfChanges: {
            dimensions,
            viewDuration,
            eegConfig: {
                LFF: config.eegConfig.LFF,
                HFF: config.eegConfig.HFF,
                notch: config.eegConfig.notch
            },
            overlayEEGConfig: {
                LFF: config.overlayEEGConfig.LFF,
                HFF: config.overlayEEGConfig.HFF,
                notch: config.overlayEEGConfig.notch
            },
            channels: config.montage?.channels.map(({ first, second }) => `${first}-${second}`),
        },
        clearRenderCacheIfChanges: {
            eegSensitivity: config.eegConfig.sensitivityMicroVolts,
            overlaySensitivity: config.overlayEEGConfig.sensitivityMicroVolts,
        }
    })

    useD3UpdateVisibleTraces({
        d3Controller,
        windowId: props.windowId
    })

    useD3KeyboardShortcuts({
        d3Controller,
        windowId: props.windowId,
        shortcuts: [
            hotkeyActions.NEXT_PAGE,
            hotkeyActions.PREVIOUS_PAGE,
            hotkeyActions.UPDATE_CUSTOM_ANNOTATION_MARKERS,
            hotkeyActions.CANCEL_ANNOTATION,
            hotkeyActions.PLAY_PAUSE
        ]
    })

    return (
        <div ref={domNode} style={{ height: "100%" }}>
            {AnnotationContextMenu}
        </div>
    )
}
